import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

// Split contexts for better performance
type DrawerStateDataContextType = {
  poiDrawerOpen: boolean;
  heatmapDrawerOpen: boolean;
};

type DrawerStateActionsContextType = {
  togglePoiDrawer: () => void;
  toggleHeatmapDrawer: () => void;
  setPoiDrawerOpen: (isOpen: boolean) => void;
  setHeatmapDrawerOpen: (isOpen: boolean) => void;
};

export const DrawerStateDataContext =
  createContext<DrawerStateDataContextType | null>(null);
export const DrawerStateActionsContext =
  createContext<DrawerStateActionsContextType | null>(null);

interface Props {
  children: React.ReactNode;
}

const STORAGE_KEYS = {
  poi: 'lcpn-drawer-poi-open',
  heatmap: 'lcpn-drawer-heatmap-open',
};

export const useDrawerStateData = () => {
  const context = useContext(DrawerStateDataContext);

  if (!context) {
    throw new Error(
      'useDrawerStateData: Used outside of the DrawerStateProvider',
    );
  }

  return context;
};

export const useDrawerStateActions = () => {
  const context = useContext(DrawerStateActionsContext);

  if (!context) {
    throw new Error(
      'useDrawerStateActions: Used outside of the DrawerStateProvider',
    );
  }

  return context;
};

// Convenience hook for components that need both
export const useDrawerState = () => {
  return {
    ...useDrawerStateData(),
    ...useDrawerStateActions(),
  };
};

export function DrawerStateProvider(props: Props) {
  const [poiDrawerOpen, setPoiDrawerOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.poi);
    return stored === null ? true : JSON.parse(stored);
  });

  const [heatmapDrawerOpen, setHeatmapDrawerOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.heatmap);
    return stored === null ? true : JSON.parse(stored);
  });

  // Persist POI drawer state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.poi, JSON.stringify(poiDrawerOpen));
  }, [poiDrawerOpen]);

  // Persist HeatMap drawer state to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.heatmap,
      JSON.stringify(heatmapDrawerOpen),
    );
  }, [heatmapDrawerOpen]);

  const togglePoiDrawer = useCallback(() => {
    setPoiDrawerOpen(prev => !prev);
  }, []);

  const toggleHeatmapDrawer = useCallback(() => {
    setHeatmapDrawerOpen(prev => !prev);
  }, []);

  const dataValue = useMemo(
    () => ({
      poiDrawerOpen,
      heatmapDrawerOpen,
    }),
    [poiDrawerOpen, heatmapDrawerOpen],
  );

  const actionsValue = useMemo(
    () => ({
      togglePoiDrawer,
      toggleHeatmapDrawer,
      setPoiDrawerOpen,
      setHeatmapDrawerOpen,
    }),
    [togglePoiDrawer, toggleHeatmapDrawer],
  );

  return (
    <DrawerStateDataContext.Provider value={dataValue}>
      <DrawerStateActionsContext.Provider value={actionsValue}>
        {props.children}
      </DrawerStateActionsContext.Provider>
    </DrawerStateDataContext.Provider>
  );
}
